/**
 * External Imports
 */
import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Internal Imports
 */
import PatientProfileHeader from '../../components/profileHeaders/patientProfileHeader';
import Round from '../../components/round';
import Loading from '../../components/shared/Loading';
import HistorySideDrawer from '../../components/sideNav/historySideDrawer';
import HistorySideNav from '../../components/sideNav/historySideNav';

import { RootState } from '../../redux/store';
import {
  getPatientRoundHistory,
  getRound,
  getTasksByAccountNum,
  clearPatientRoundHistory
} from '../../redux/actions';
import { useGetPatientToRoundQuery } from '../../redux/apis/Users.api';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Feedback,
  Issue,
  RoundHistory
} from '../../config/interfaces';

/**
 * Style Imports
 */
import { ProfileContainer } from './styles/PatientViews.styles';

interface PatientProfileProps {
  authorizedUser?: AuthUserProps;
  patientRoundHistory?: RoundHistory[];
  tasks?: (Issue | Feedback)[];
}

const PatientProfile: FC<PatientProfileProps> = ({
  authorizedUser,
  patientRoundHistory,
  tasks
}) => {
  const dispatch = useDispatch();

  const [selectedTab, setSelectedTab] = useState<string>('');
  // const [sideDrawerData, setSideDrawerData] = useState<any>({});

  const { state } = useLocation();
  const navigate = useNavigate();
  const { patientMeta, isUnlistedPatient = false } =
    (state as {
      patientMeta?: {
        facilityId: string;
        unitId?: string;
        urn?: string;
        mrn?: string;
        accountNum: string;
        room?: string;
        bed?: string;
        firstName?: string;
        lastName?: string;
        lastRoundId?: string;
      } | null;
      isUnlistedPatient?: boolean;
    }) || {};
  const {
    data: patientToRoundData,
    // isError: isGetPatientToRoundError,
    // isFetching: isGetPatientToRoundFetching,
    isLoading: isGetPatientToRoundLoading
  } = useGetPatientToRoundQuery(patientMeta!, {
    skip: !patientMeta || isUnlistedPatient
  });

  useLayoutEffect(() => {
    if (!isUnlistedPatient && !patientMeta) {
      // TODO: Hack to return to patient census when refreshing or navigating straight to profile
      navigate('/patients/census');
    }

    // Perform layout effect logic here
    // Get patient round history
    if (
      authorizedUser?.facilityId &&
      (patientToRoundData?.urn || patientMeta?.urn)
    )
      dispatch(
        getPatientRoundHistory({
          facilityId: authorizedUser?.facilityId ?? '',
          patientId: patientToRoundData?.urn || patientMeta?.urn
        })
      );
    // Get issues and feedback associated with patient
    if (
      authorizedUser?.facilityId &&
      (patientToRoundData?.accountNum || patientMeta?.accountNum)
    )
      dispatch(
        getTasksByAccountNum({
          accountNumber:
            patientToRoundData?.accountNum || patientMeta?.accountNum,
          facilityMnemonic: authorizedUser?.facilityId
        })
      );
  }, [patientMeta, patientToRoundData?.accountNum, patientToRoundData?.urn]);

  useEffect(() => {
    if (isUnlistedPatient) {
      if (patientMeta?.lastRoundId)
        dispatch(
          getRound({
            roundId: patientMeta?.lastRoundId,
            fromHistoryDrawer: false,
            type: 'patient',
            accountNum: patientMeta.accountNum,
            unitId: patientMeta?.unitId
          })
        );
    }
    return () => {
      dispatch(clearPatientRoundHistory());
    };
  }, []);

  return (
    <ProfileContainer
      id="patient-profile"
      data-testid="patient-profile"
      className="neu-bg-plain-0"
    >
      {isGetPatientToRoundLoading ? (
        <Loading centered label="patient data..." />
      ) : (
        <>
          <section>
            <PatientProfileHeader
              selectedPatient={
                isUnlistedPatient || patientToRoundData?.discharged
                  ? patientMeta
                  : patientToRoundData
              }
            />
          </section>
          <section className="d-flex" style={{ height: 'calc(100vh - 163px)' }}>
            <HistorySideNav
              selectedPatient={
                isUnlistedPatient || patientToRoundData?.discharged
                  ? patientMeta
                  : patientToRoundData
              }
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              type="patient"
            />
            <HistorySideDrawer
              selectedPerson={
                isUnlistedPatient || patientToRoundData?.discharged
                  ? patientMeta
                  : patientToRoundData
              }
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              roundHistory={patientRoundHistory}
              tasks={tasks}
              type="patient"
            />
            <Round
              type="patient"
              selectedPatient={
                isUnlistedPatient || patientToRoundData?.discharged
                  ? patientMeta
                  : patientToRoundData
              }
            />
          </section>
        </>
      )}
    </ProfileContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  authorizedUser: state.AuthorizedUser.authorizedUser,
  isRoundStarted: state.RoundReducer.isRoundStarted,
  patientRoundHistory: state.ReportReducer.patientRoundHistory,
  tasks: state.TaskReducer.patientTasks
});

export default connect(mapReduxStateToProps)(PatientProfile);
